import React, { useState } from "react";
import { X, Zap } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import Badge from "../common/Badge";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import { CREDIT_PACKAGES } from "../../config/creditPackages";

const starterRate = CREDIT_PACKAGES[0].priceAUD / CREDIT_PACKAGES[0].credits;

function savingPct(pkg) {
  if (pkg.id === "starter") return null;
  const rate = pkg.priceAUD / pkg.credits;
  return Math.round((1 - rate / starterRate) * 100);
}

export default function BuyCredits({ businessId, creditBalance, onClose }) {
  const { showToast } = useToast();
  const [buying, setBuying] = useState(null);

  async function handleBuyNow(pkg) {
    if (!businessId) {
      showToast("No business loaded.", "error");
      return;
    }
    setBuying(pkg.id);
    try {
      const returnUrl = window.location.href.split("?")[0];
      const { data, error } = await supabase.functions.invoke(
        "create-checkout-session",
        {
          body: {
            packageId: pkg.id,
            businessId,
            returnUrl,
          },
        }
      );
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned.");
      }
    } catch (err) {
      showToast(`Failed to start checkout: ${err.message}`, "error");
      setBuying(null);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 1000,
        overflowY: "auto",
        padding: "2rem 1rem",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ maxWidth: "860px", margin: "0 auto" }}>
        <Card>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "1.25rem",
            }}
          >
            <div>
              <h2>
                <Zap size={18} /> Top up credits
              </h2>
              <p className="muted">
                Current balance:{" "}
                <strong>{creditBalance.toLocaleString()} credits</strong>
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                color: "var(--muted, #666)",
              }}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            {CREDIT_PACKAGES.map((pkg) => {
              const rate = pkg.priceAUD / pkg.credits;
              const saving = savingPct(pkg);
              const isActive = buying === pkg.id;

              return (
                <div
                  key={pkg.id}
                  style={{
                    border: pkg.popular
                      ? "2px solid var(--accent, #FF8A00)"
                      : "1px solid var(--border, #e5e7eb)",
                    borderRadius: "12px",
                    padding: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.4rem",
                    position: "relative",
                    background: pkg.popular
                      ? "var(--accent-bg, #eff6ff)"
                      : "var(--surface, #fff)",
                  }}
                >
                  {pkg.popular && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-12px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    >
                      <Badge type="info">Most Popular</Badge>
                    </div>
                  )}

                  <div
                    className="feature-title"
                    style={{ fontWeight: 700, fontSize: "1rem" }}
                  >
                    {pkg.name}
                  </div>

                  <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>
                    {pkg.credits.toLocaleString()}
                    <span className="muted small"> credits</span>
                  </div>

                  <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>
                    ${pkg.priceAUD} AUD
                    {pkg.perSeat && (
                      <span className="muted small"> /seat</span>
                    )}
                  </div>

                  <div className="muted small">
                    ${rate.toFixed(3)} per credit
                  </div>

                  {saving !== null && saving > 0 && (
                    <Badge type="success">{saving}% saving</Badge>
                  )}

                  <div style={{ marginTop: "auto", paddingTop: "0.75rem" }}>
                    <Button
                      onClick={() => handleBuyNow(pkg)}
                      disabled={!!buying}
                      style={{ width: "100%" }}
                    >
                      {isActive ? "Opening…" : "Buy now"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="muted small">
            All prices exclude GST. Credits never expire.
          </p>
        </Card>
      </div>
    </div>
  );
}
